import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, TextField, Button, Typography, Alert, InputAdornment, IconButton, useTheme } from '@mui/material';
import { Visibility, VisibilityOff, LockOutlined, EmailOutlined, Key } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppHooks';
import { login, clearError, clear2FA, clearPasswordChange, changeForcedPassword } from '../../features/auth/authSlice';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isLoading, error, isAuthenticated, requires2FA, requiresPasswordChange } =
    useAppSelector((state) => state.auth);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => { dispatch(clearError()); dispatch(clear2FA()); dispatch(clearPasswordChange()); };
  }, [dispatch]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (requiresPasswordChange) {
      if (newPassword !== confirmPassword) return;          // disable handles this case already
      if (newPassword.length < 12) return;
      dispatch(changeForcedPassword({ newPassword }));
      return;
    }
    if (requires2FA) {
      dispatch(login({ email, password, totp_code: totpCode }));
    } else {
      dispatch(login({ email, password }));
    }
  };

  const handleBack = () => {
    dispatch(clear2FA());
    dispatch(clearPasswordChange());
    dispatch(clearError());
    setTotpCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
      '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)' },
      '&.Mui-focused': { backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : '#ffffff' },
      '& fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)' },
      '&:hover fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' },
      '& input:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 100px ${isDark ? '#1a1f2e' : theme.palette.background.paper} inset`,
        WebkitTextFillColor: theme.palette.text.primary,
      },
    },
  };

  // Local validation surface for the password-change panel
  const pwMismatch = requiresPasswordChange && confirmPassword.length > 0 && newPassword !== confirmPassword;
  const pwTooShort = requiresPasswordChange && newPassword.length > 0 && newPassword.length < 12;
  const submitDisabled =
    isLoading ||
    (requiresPasswordChange && (newPassword.length < 12 || newPassword !== confirmPassword));

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
      <Card sx={{ width: '100%', maxWidth: 400, mx: 2, boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              component="img"
              src="/logo-wordmark.png"
              alt="Peribott"
              sx={{ height: 48, width: 'auto', maxWidth: 240, objectFit: 'contain', mb: 2, display: 'inline-block' }}
            />
            <Typography variant="body2" color="text.secondary">
              {requiresPasswordChange
                ? 'Set a new password to continue'
                : requires2FA
                  ? 'Enter your authenticator code'
                  : 'Sign in to manage your robots'}
            </Typography>
          </Box>
          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
          <form onSubmit={handleSubmit}>
            {requiresPasswordChange ? (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Your one-time password must be changed before you can use this account.
                  Minimum 12 characters, with upper + lower case, a digit, and a symbol.
                </Alert>
                <TextField
                  fullWidth label="New Password" type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  sx={{ mb: 2, ...fieldSx }}
                  error={pwTooShort}
                  helperText={pwTooShort ? 'At least 12 characters' : ' '}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LockOutlined sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>,
                    endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowNew(!showNew)} edge="end" size="small">{showNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment>
                  }}
                  autoFocus required
                />
                <TextField
                  fullWidth label="Confirm New Password" type={showNew ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  sx={{ mb: 3, ...fieldSx }}
                  error={pwMismatch}
                  helperText={pwMismatch ? 'Passwords do not match' : ' '}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LockOutlined sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>,
                  }}
                  required
                />
              </>
            ) : !requires2FA ? (
              <>
                <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} sx={{ mb: 2, ...fieldSx }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlined sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment> }} required />
                <TextField fullWidth label="Password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} sx={{ mb: 3, ...fieldSx }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LockOutlined sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>,
                    endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">{showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment>
                  }} required />
              </>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Open your authenticator app and enter the 6-digit code. You can also use a recovery code.
                </Alert>
                <TextField
                  fullWidth label="Authentication Code" value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  sx={{ mb: 3, ...fieldSx }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Key sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment> }}
                  autoFocus required placeholder="000000"
                  inputProps={{ maxLength: 8, style: { letterSpacing: '0.3em', fontFamily: 'monospace', fontSize: '1.2em' } }}
                />
              </>
            )}
            <Button type="submit" variant="contained" fullWidth size="large" disabled={submitDisabled} sx={{ py: 1.5 }}>
              {isLoading ? 'Working...' : requiresPasswordChange ? 'Set New Password' : requires2FA ? 'Verify' : 'Sign In'}
            </Button>
            {(requires2FA || requiresPasswordChange) && (
              <Button fullWidth sx={{ mt: 1 }} onClick={handleBack} disabled={isLoading}>
                Back to Login
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
